import { Chart, registerables } from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { DemoCard } from './PerceptionDemoUI';

Chart.register(...registerables);

export const DEMO_CHART_COLORS = {
  blue: '#4164FD',
  teal: '#4CBEBB',
  orange: '#F28725',
  purple: '#9B6CFF',
  pink: '#F05A82',
  yellow: '#FFCE60',
  light: '#DADADA',
};

const axis = (percentage = false) => ({
  beginAtZero: false,
  border: { display: false },
  grid: { color: 'rgba(255,255,255,0.055)' },
  ticks: { color: '#777777', maxTicksLimit: 6, callback: percentage ? (value) => `${value}%` : undefined },
});

const legend = {
  display: true,
  position: 'top',
  align: 'end',
  labels: { color: '#929292', boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: 'circle', padding: 15 },
};

export const DemoLineChart = ({ title, subtitle, labels, series, height = 250, percentage = false, min = undefined, max = undefined, right }) => {
  const data = {
    labels,
    datasets: series.map(({ name, values, color, fill = false, dashed = false }) => ({
      label: name,
      data: values,
      borderColor: color,
      backgroundColor: fill ? `${color}23` : color,
      pointBackgroundColor: color,
      pointRadius: labels.length > 31 ? 0 : 2,
      pointHoverRadius: 4,
      borderWidth: 2,
      borderDash: dashed ? [5, 5] : [],
      tension: 0.35,
      spanGaps: true,
      fill,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend, tooltip: { backgroundColor: '#141414', titleColor: '#E8E8E8', bodyColor: '#B6B3B3', borderColor: '#555555', borderWidth: 1 } },
    scales: {
      x: { ...axis(), ticks: { color: '#777777', maxTicksLimit: 8, maxRotation: 0 }, grid: { display: false } },
      y: { ...axis(percentage), ...(min !== undefined ? { min } : {}), ...(max !== undefined ? { max } : {}) },
    },
  };

  return <DemoCard title={title} subtitle={subtitle} right={right}><div style={{ height }}><Line data={data} options={options}/></div></DemoCard>;
};

export const DemoBarChart = ({ title, subtitle, labels, series, height = 255, horizontal = false, stacked = false, percentage = false, right }) => {
  const data = {
    labels,
    datasets: series.map(({ name, values, color }) => ({ label: name, data: values, backgroundColor: color, borderRadius: 4, maxBarThickness: horizontal ? 18 : 34 })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { ...legend, display: series.length > 1 }, tooltip: { backgroundColor: '#141414' } },
    scales: {
      x: { ...axis(horizontal && percentage), stacked, beginAtZero: true, grid: { display: horizontal } },
      y: { ...axis(!horizontal && percentage), stacked, beginAtZero: !horizontal, grid: { display: !horizontal } },
    },
  };

  return <DemoCard title={title} subtitle={subtitle} right={right}><div style={{ height }}><Bar data={data} options={options}/></div></DemoCard>;
};

export const DemoDonutChart = ({ title, subtitle, segments, centerValue, centerLabel, height = 245 }) => {
  const data = {
    labels: segments.map(({ name }) => name),
    datasets: [{ data: segments.map(({ count }) => count), backgroundColor: segments.map(({ color }) => color), borderColor: '#2D2C2C', borderWidth: 3, hoverOffset: 5 }],
  };
  const options = { responsive: true, maintainAspectRatio: false, cutout: '76%', plugins: { legend: { display: false } } };

  return (
    <DemoCard title={title} subtitle={subtitle}>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-center">
        <div className="relative mx-auto w-full max-w-[260px]" style={{ height }}>
          <Doughnut data={data} options={options}/>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl text-perception-gray-100">{centerValue}</span>
            <span className="mt-1 text-center text-xs text-perception-gray-600">{centerLabel}</span>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          {segments.map(({ name, count, color }) => (
            <div key={name} className="flex items-center justify-between gap-4 text-xs">
              <span className="flex items-center gap-2 text-perception-gray-400"><span className="size-2.5 shrink-0 rounded-sm" style={{ backgroundColor: color }}/>{name}</span>
              <span className="font-semibold text-perception-gray-300">{count.toLocaleString('en-GB')}</span>
            </div>
          ))}
        </div>
      </div>
    </DemoCard>
  );
};
